import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@startupquickstart/frontend';

import type { Event } from '@/config/objects/events';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Not set';
  return new Date(dateString).toLocaleString();
}

/** A labelled schedule value; `muted` styles the "not yet" placeholders. */
function ScheduleRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-1">{label}</p>
      <p className={muted ? 'text-sm italic text-muted-foreground' : 'text-sm font-medium'}>
        {value}
      </p>
    </div>
  );
}

/**
 * Planned vs actual schedule card — keeps the *planned* timing (the intended
 * start/end the admin set) visually distinct from the *actual* lifecycle
 * timestamps (stamped when the event is Started/Ended), since they can
 * legitimately differ. Shared by the Overview and Live tabs.
 *
 * @param props - Event whose schedule to display.
 */
export function EventScheduleCard({ event }: { event: Event }) {
  return (
    <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
      <CardHeader className="px-6 py-4 !pb-4 border-b">
        <CardTitle>Schedule</CardTitle>
        <CardDescription>Planned timing vs. when the event actually ran</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Planned
            </p>
            <ScheduleRow label="Planned Start" value={formatDate(event.startTime)} />
            <ScheduleRow label="Planned End" value={formatDate(event.endTime)} />
            <ScheduleRow label="Timezone" value={event.timezone} />
          </div>
          <div className="space-y-4">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Actual
            </p>
            <ScheduleRow
              label="Started"
              value={event.startedAt ? formatDate(event.startedAt) : 'Not started yet'}
              muted={!event.startedAt}
            />
            <ScheduleRow
              label="Ended"
              value={event.endedAt ? formatDate(event.endedAt) : 'Not ended yet'}
              muted={!event.endedAt}
            />
          </div>
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          Planned times are the intended schedule. Actual times are stamped when you Start and End
          the event with the lifecycle controls — so an event can be running before its planned
          start, or still planned with no actual start yet.
        </p>
      </CardContent>
    </Card>
  );
}
